import {Collaborator, DataCollaborator, OwnerInfo} from 'auth/PermissionConstants';
import * as net from 'util/Net';
import {
  ExperimentalCondition,
  StageConfiguration,
  WorkflowDetails,
  WorkflowTemplate,
  WorkflowTemplateVersionDetails,
  WorkflowTemplateVersionSummary,
} from 'workflows/WorkflowConstants';

export interface FetchAllWorkflowTemplatesResponse {
  workflowTemplates: Array<WorkflowTemplate>;
}

export function fetchAllWorkflowTemplates(): JQueryPromise<FetchAllWorkflowTemplatesResponse> {
  return net.getJSON('/1/api/workflows/workflow-templates');
}

export interface FetchWorkflowTemplateResponse {
  workflowTemplate: WorkflowTemplate;
}

export function fetchWorkflowTemplate(
  workflowTemplateId: string
): JQueryPromise<FetchWorkflowTemplateResponse> {
  return net.getJSON(`/1/api/workflows/workflow-templates/${workflowTemplateId}`);
}

export function createWorkflowTemplate({
  name,
  owner,
  memberDataPolicyId,
  prefix,
  collaborators,
  workflowTemplateVersionId,
}: {
  name: string;
  owner: string;
  memberDataPolicyId: string | null;
  prefix: string;
  collaborators: Array<DataCollaborator>;
  workflowTemplateVersionId?: string;
}): JQueryPromise<FetchWorkflowTemplateResponse> {
  return net.postJSON({
    url: '/1/api/workflows/workflow-templates',
    data: {
      name,
      owner,
      ownerInfo: {memberDataPolicyId},
      prefix,
      collaborators,
      workflowTemplateVersionId,
    },
  });
}

export function fetchCollaborators(workflowTemplateId: string): JQueryPromise<Array<Collaborator>> {
  return net.getJSON(`/1/api/workflow-templates/${workflowTemplateId}/collaborators/`);
}

export interface CreateWorkflowTemplateVersionResponse extends WorkflowTemplateVersionDetails {
  workflowTemplate: WorkflowTemplate;
}

export function createWorkflowTemplateVersion(
  workflowTemplateId: string,
  workflowTemplateVersionId: string
): JQueryPromise<CreateWorkflowTemplateVersionResponse> {
  return net.postJSON({
    url: '/1/api/workflows/workflow-template-versions',
    data: {
      workflowTemplateId,
      workflowTemplateVersionId,
    },
  });
}

export interface CreateStageConfigurationResponse extends WorkflowTemplateVersionDetails {
  stageConfiguration: StageConfiguration;
  workflowDetails: WorkflowDetails | null;
}

export function createStageConfiguration({
  workflowTemplateVersionId,
  stageName,
  entryTemplateName,
  stageConfigurationIdForEntryTemplate,
  experimentalConditions,
  index,
}: {
  workflowTemplateVersionId: string;
  stageName: string;
  entryTemplateName: string;
  stageConfigurationIdForEntryTemplate: string | null;
  experimentalConditions: Array<ExperimentalCondition>;
  index: number | null | undefined;
}): JQueryPromise<CreateStageConfigurationResponse> {
  return net.postJSON({
    url: '/1/api/workflows/stage-configurations',
    data: {
      workflowTemplateVersionId,
      stageName,
      entryTemplateName,
      stageConfigurationIdForEntryTemplate,
      experimentalConditions,
      index,
    },
  });
}

export interface DeleteStageConfigurationResponse extends WorkflowTemplateVersionDetails {
  deletedEntryIds: Array<string>;
  workflowDetails: WorkflowDetails | null;
}

export function deleteStageConfiguration(
  stageConfigurationId: string
): JQueryPromise<DeleteStageConfigurationResponse> {
  return net.delete_({url: `/1/api/workflows/stage-configurations/${stageConfigurationId}`});
}

export interface EditStageConfigurationResponse extends WorkflowTemplateVersionDetails {
  stageConfiguration: StageConfiguration;
  workflowDetails: WorkflowDetails | null;
  deletedEntryIds: Array<string>;
}

export function editStageConfiguration(
  stageConfigurationId: string,
  {
    stageName,
    experimentalConditions,
    stageConfigurationIdForEntryTemplate,
  }: {
    stageName: string;
    experimentalConditions: Array<ExperimentalCondition>;
    stageConfigurationIdForEntryTemplate: string | null;
  }
): JQueryPromise<EditStageConfigurationResponse> {
  return net.patchJSON({
    url: `/1/api/workflows/stage-configurations/${stageConfigurationId}`,
    data: {stageName, experimentalConditions, stageConfigurationIdForEntryTemplate},
  });
}

export function reorderStageConfiguration(
  stageConfigurationId: string,
  newIndex: number
): JQueryPromise<WorkflowTemplateVersionDetails> {
  return net.postJSON({
    url: `/1/api/workflows/stage-configurations/${stageConfigurationId}:reorder`,
    data: {newIndex},
  });
}

export function fetchWorkflowTemplateVersionDetails(
  workflowTemplateVersionId: string
): Promise<WorkflowTemplateVersionDetails> {
  return net.getJSONPromise(
    `/1/api/workflows/workflow-template-versions/${workflowTemplateVersionId}/details`
  );
}

export interface EditWorkflowTemplateVersionMetadataResponse {
  workflowTemplateVersionSummary: WorkflowTemplateVersionSummary;
}

export function editWorkflowTemplateVersionMetadata(
  workflowTemplateVersionId: string,
  {description}: {description: string}
): JQueryPromise<EditWorkflowTemplateVersionMetadataResponse> {
  return net.patchJSON({
    url: `/1/api/workflows/workflow-template-versions/${workflowTemplateVersionId}`,
    data: {description},
  });
}

export function deleteWorkflowTemplate(workflowId: string): JQueryPromise<{}> {
  return net.delete_({
    url: `/1/api/workflows/workflow-templates/${workflowId}`,
  });
}

export interface EditWorkflowTemplateMetadataResponse {
  workflowTemplate: WorkflowTemplate;
  collaborators: Array<Collaborator>;
}

export function editWorkflowTemplateMetadata(
  workflowId: string,
  {
    name,
    owner,
    ownerInfo,
    prefix,
    collaborators,
  }: {
    name: string;
    owner: string;
    ownerInfo: OwnerInfo;
    prefix: string;
    collaborators: Array<Collaborator>;
  }
): JQueryPromise<EditWorkflowTemplateMetadataResponse> {
  return net.patchJSON({
    url: `/1/api/workflows/workflow-templates/${workflowId}`,
    data: {name, owner, ownerInfo, prefix, collaborators},
  });
}

export function publishDraft(
  workflowTemplateVersionId: string
): JQueryPromise<WorkflowTemplateVersionDetails> {
  return net.postJSON({
    url: `/1/api/workflows/workflow-template-versions/${workflowTemplateVersionId}/publish`,
    data: {},
  });
}
